// src/components/TaskSplitDetail.tsx
// Compact task detail panel designed for the split view context.
// Uses an instance-local request; mounted previews never share currentItem.

import { useEffect, useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useTask, useTaskStatusDefinition, useI18n } from '@react/hooks';
import { ErrorAlert } from '@react/components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@core/ui';
import { entityStore } from '@/stores/EntityStore';
import { useTabId } from '@/components/tabs/TabContext';
import { useResolvedUserName } from '@/features/userprofile/UserName';
import { useParentTitles } from '@/features/task/useParentTitles';
import { PropertiesPanel } from '@/components/PropertiesPanel';
import { StatusSelect } from '@/components/StatusSelect';
import { PrioritySelect } from '@/components/PrioritySelect';
import { TaskSectionsProvider, useMissingSections } from '@/features/task/detail/sections';
import { SectionAddBar } from '@/features/task/detail/TaskSection';
import { TaskChecklists } from '@/features/task/TaskChecklists';
import { TaskSubtasks } from '@/features/task/TaskSubtasks';
import { TaskTimeTracking } from '@/features/task/TaskTimeTracking';
import { TaskActivityFeed } from '@/features/task/TaskActivityFeed';
import { useTaskDetail } from '@/features/task/useTaskDetail';
import { TaskDependencies } from '@/features/task/TaskDependencies';
import { TaskAttachments } from '@/features/task/TaskAttachments';
import { entityToUrn } from '@/features/resources/logic';
import {
  buildStatusChangeRequest,
  buildTaskFieldUpdate,
  getTaskStatus,
  getTaskPriority,
  getTaskAssignee,
  getTaskDueDate,
  getTaskTags,
  getTaskProgress,
  getTaskActualDuration,
  getTaskEstimatedDuration,
  getTaskWatchers,
  getTaskParent,
  getTaskKey,
} from '@/features/task/logic';

import type { Task } from '@core/types/task';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TaskSplitDetailProps {
  taskId: string;
  /** Embedded hosts supply their own persistent navigation/close toolbar. */
  showHeader?: boolean;
  keyboardShortcuts?: boolean;
  onClose: () => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  /** Notifies the host list so the row can reflect an edit made here. */
  onUpdated?: (taskId: string, patch: Record<string, unknown>) => void;
}

// ---------------------------------------------------------------------------
// Inline icons
// ---------------------------------------------------------------------------

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/** Arrow leaving a box — "open this on its own page". */
function OpenFullIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TaskSplitDetail = observer(function TaskSplitDetail({
  taskId,
  onClose,
  onNavigate,
  onUpdated,
  showHeader = true,
  keyboardShortcuts = true,
}: TaskSplitDetailProps) {
  const store = useTask();
  const statusDefStore = useTaskStatusDefinition();
  const { t } = useI18n();
  const tabId = useTabId();

  const [activityRefresh, setActivityRefresh] = useState(0);
  const detail = useTaskDetail(taskId);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  // The `s` / `p` shortcuts open the pickers by clicking their triggers.
  // StatusSelect / PrioritySelect own their open state, so the wrapper is the
  // only handle available without changing those shared components.
  const statusPickerRef = useRef<HTMLDivElement>(null);
  const priorityPickerRef = useRef<HTMLDivElement>(null);

  const item = detail.task as any;

  const resolvedCreatorName = useResolvedUserName(
    item?.creator?.id || item?.creator?.entity?.id,
  );
  const parentRef = getTaskParent(item);
  const parentTitles = useParentTitles(parentRef ? [parentRef] : []);

  // Custom statuses drive the status picker; the workspace usually already
  // loaded them, so only fetch when this panel is used stand-alone.
  useEffect(() => {
    if (statusDefStore.items.length === 0) {
      statusDefStore.ListStatusDefinitions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    if (entityStore.stateFor(tabId).currentEntity?.id === taskId) entityStore.clearContext(tabId);
  }, [taskId, tabId, detail.key]);

  // ── Load entity context for Right Rail ────────────────────────────────
  // Task responses carry no `urn` field — compose it from the entity's parts,
  // the way the detail page does, or the attachments section reads an empty
  // context.
  useEffect(() => {
    if (item?.entity?.id === taskId) {
      entityStore.loadEntityData(tabId, {
        urn: item.entity.urn || entityToUrn(item.entity),
        type: item.entity.type_name || 'task',
        id: item.entity.id || taskId,
        title: item.title || '',
      });
    }
  }, [item?.entity?.id, item?.title, taskId, tabId]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Skip when user is typing in an input
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      // Leave browser/OS chords alone (⌘S, Ctrl+P, ...)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case '[':
          e.preventDefault();
          onNavigate?.('prev');
          break;
        case ']':
          e.preventDefault();
          onNavigate?.('next');
          break;
        case 's':
        case 'S':
          e.preventDefault();
          statusPickerRef.current?.querySelector('button')?.click();
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          priorityPickerRef.current?.querySelector('button')?.click();
          break;
        default:
          break;
      }
    },
    [onNavigate],
  );

  useEffect(() => {
    if (!keyboardShortcuts) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, keyboardShortcuts]);

  // ── Fresh refetch ─────────────────────────────────────────────────────
  // Mutation responses update the store cache with partial task copies (no
  // checklists), so every refetch here must bypass the cache or the sections
  // below lose their data after any edit.
  const refreshTask = detail.reload;

  // ── Status change handler ─────────────────────────────────────────────
  const handleStatusChange = useCallback(
    async (newStatus: string) => {
      if (!taskId) return;
      const request = buildStatusChangeRequest(taskId, newStatus, statusDefStore.items);
      try {
        await store.ChangeTaskStatus(taskId, request);
        onUpdated?.(taskId, { status: request.status, custom_status: request.custom_status || '' });
        refreshTask();
      } catch {
        /* store handles error */
      }
    },
    [store, statusDefStore.items, taskId, onUpdated, refreshTask],
  );

  // ── Priority change handler ───────────────────────────────────────────
  const handlePriorityChange = useCallback(
    async (newPriority: string) => {
      if (!taskId) return;
      try {
        await store.UpdateTask(
          taskId,
          buildTaskFieldUpdate(taskId, { priority: newPriority as Task['priority'] }),
        );
        onUpdated?.(taskId, { priority: newPriority });
        refreshTask();
      } catch {
        /* store handles error */
      }
    },
    [store, taskId, onUpdated, refreshTask],
  );

  // ── Title editing ─────────────────────────────────────────────────────
  const startEditTitle = useCallback(() => {
    setEditTitle(item?.title || '');
    setIsEditingTitle(true);
  }, [item?.title]);

  useEffect(() => {
    if (isEditingTitle) titleInputRef.current?.focus();
  }, [isEditingTitle]);

  const commitTitle = useCallback(async () => {
    setIsEditingTitle(false);
    const title = editTitle.trim();
    if (!title || title === item?.title) return;
    try {
      await store.UpdateTask(taskId, buildTaskFieldUpdate(taskId, { title }));
      onUpdated?.(taskId, { title });
      refreshTask();
    } catch {
      /* store handles error */
    }
  }, [editTitle, item?.title, store, taskId, onUpdated, refreshTask]);

  // ── Loading state ─────────────────────────────────────────────────────
  // Only the initial task fetch gates the panel. `store.isLoading` flips for
  // every store action — checklist toggles, dependency edits — and gating on
  // it would bounce the whole panel into the skeleton on each of those.
  if (!item && detail.isLoading) {
    return (
      <div className="p-4 space-y-4 animate-pulse">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded bg-muted" />
          <div className="h-4 w-20 rounded bg-muted" />
        </div>
        <div className="h-6 w-3/4 rounded bg-muted" />
        <div className="h-4 w-1/2 rounded bg-muted" />
        <div className="space-y-2 mt-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex justify-between">
              <div className="h-4 w-24 rounded bg-muted" />
              <div className="h-4 w-32 rounded bg-muted" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────
  if (detail.error && !item) {
    return (
      <div className="p-4">
        <ErrorAlert error={detail.error} dismissible onDismiss={detail.clearError} />
      </div>
    );
  }

  // ── Not found ─────────────────────────────────────────────────────────
  if (!item) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p>{t('common.notFound')}</p>
      </div>
    );
  }

  // GetTask returns fields flat on the task object; read through the shared
  // accessors, which also cover the nested list-row shape.
  const status = getTaskStatus(item);
  const assignee = getTaskAssignee(item);
  const parent = getTaskParent(item);
  const creatorName = item?.creator?.display_name || item?.creator?.name || resolvedCreatorName;

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full flex-col">
      {/* ── Header ──────────────────────────────────────────────────── */}
      {showHeader && <div className="flex items-center gap-1 border-b border-border px-3 py-2 shrink-0">
        {/* Prev / Next navigation */}
        {onNavigate && (
          <>
            <button
              type="button"
              onClick={() => onNavigate('prev')}
              className={cn(
                'inline-flex items-center justify-center rounded p-1',
                'text-muted-foreground hover:text-foreground hover:bg-muted',
                'transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
              aria-label={t('common.previous')}
              title="["
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onNavigate('next')}
              className={cn(
                'inline-flex items-center justify-center rounded p-1',
                'text-muted-foreground hover:text-foreground hover:bg-muted',
                'transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
              aria-label={t('common.next')}
              title="]"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </>
        )}

        {/* Identifier — also a way out to the full page. Falls back to the id
            prefix the list rows show, so the link is always there. */}
        <Link
          to={`/tasks/${taskId}`}
          className="ml-1 rounded px-1 text-xs font-mono text-muted-foreground hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {getTaskKey(item) || t('task.unnumbered')}
        </Link>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Open on its own page — the panel is a preview, not the whole task */}
        <Link
          to={`/tasks/${taskId}`}
          className={cn(
            'inline-flex items-center justify-center rounded p-1',
            'text-muted-foreground hover:text-foreground hover:bg-muted',
            'transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
          aria-label={t('task.openFull')}
          title={t('task.openFull')}
        >
          <OpenFullIcon className="h-4 w-4" />
        </Link>

        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className={cn(
            'inline-flex items-center justify-center rounded p-1',
            'text-muted-foreground hover:text-foreground hover:bg-muted',
            'transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
          aria-label={t('common.close')}
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>}

      {/* ── Scrollable body ─────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {/* Title + status */}
          <div className="space-y-1.5">
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                type="text"
                value={editTitle}
                aria-label={t('task.field.title')}
                onChange={(e) => setEditTitle(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitTitle();
                  if (e.key === 'Escape') setIsEditingTitle(false);
                }}
                className={cn(
                  'w-full text-lg font-semibold text-foreground bg-transparent',
                  'border-b-2 border-primary outline-none',
                )}
              />
            ) : (
              <h2
                className="text-lg font-semibold text-foreground cursor-pointer hover:text-primary transition-colors"
                onDoubleClick={startEditTitle}
                title={t('task.doubleClickToEdit')}
              >
                {String(item.title ?? '')}
              </h2>
            )}

            {/* Status / priority pickers — also reachable with `s` and `p` */}
            <div className="flex items-center gap-2 pt-0.5">
              <div ref={statusPickerRef} title={t('task.shortcut.status')}>
                <StatusSelect value={status || ''} onSelect={handleStatusChange} />
              </div>
              <div ref={priorityPickerRef} title={t('task.shortcut.priority')}>
                <PrioritySelect value={getTaskPriority(item) || ''} onSelect={handlePriorityChange} />
              </div>
            </div>
          </div>

          {/* ── Description ─────────────────────────────────────────── */}
          {item.description && (
            <div className="space-y-1">
              <h3 className="text-xs font-medium text-muted-foreground">
                {t('task.field.description')}
              </h3>
              <div className="prose prose-sm dark:prose-invert max-w-none text-foreground">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {String(item.description)}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* ── Properties ──────────────────────────────────────────── */}
          <PropertiesPanel
            status={status}
            priority={getTaskPriority(item)}
            assignee={
              assignee
                ? { id: assignee.entity?.id || assignee.id || '', display_name: assignee.display_name || assignee.name || '' }
                : null
            }
            dueDate={getTaskDueDate(item)}
            type={item.type}
            labels={getTaskTags(item)}
            progress={getTaskProgress(item)}
            timeTracked={getTaskActualDuration(item)}
            timeEstimated={getTaskEstimatedDuration(item)}
            createdAt={item.created_at}
            createdBy={creatorName}
            createdByActor={item.creator}
            updatedAt={item.updated_at}
            parentKey={parent?.id}
            parentTitle={parent?.id ? parentTitles[parent.id] || parent.title : undefined}
            parentType={parent?.type_name}
            watchers={getTaskWatchers(item).map((w: any) => ({
              id: w.entity?.id || w.id || '',
              display_name: w.display_name || w.name || '',
            }))}
            onStatusChange={handleStatusChange}
            className="text-sm"
          />

          {/* ── The task's parts: checklists, subtasks, time, dependencies,
                  attachments. The same sections the full page draws, gated the
                  same way — what the task has is shown, what it lacks is one
                  quiet offer row. Time tracking is included because
                  SectionAddBar offers every section it cannot see. ──────── */}
          <TaskSectionsProvider>
            <SplitSections taskId={taskId} item={item} onChanged={refreshTask} onActivityChanged={() => setActivityRefresh((value) => value + 1)} />
          </TaskSectionsProvider>

          <TaskActivityFeed taskId={taskId} compact refreshKey={activityRefresh} />
        </div>
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// The task's parts, in the detail page's order
// ---------------------------------------------------------------------------

/**
 * The sections the full task page draws, in the same order and from the same
 * components. Inside the sections provider the ones with nothing in them are
 * not drawn; they are offered as a quiet row instead. Kept out of the main
 * component because `useMissingSections` reads the provider from above.
 */
const SplitSections = observer(function SplitSections({
  taskId,
  item,
  onChanged,
  onActivityChanged,
}: {
  taskId: string;
  item: Record<string, any>;
  onChanged: () => void;
  onActivityChanged: () => void;
}) {
  const { t } = useI18n();
  const { missing, reveal } = useMissingSections();

  return (
    <div className="space-y-4">
      <TaskChecklists
        taskId={taskId}
        checklists={item.checklists?.checklists || item.checklists || []}
        onChanged={onChanged}
      />
      <TaskSubtasks parentId={taskId} parentTypeName={item.entity?.type_name || 'task'} onChanged={onActivityChanged} />
      <TaskTimeTracking taskId={taskId} />
      <TaskDependencies taskId={taskId} />
      <TaskAttachments />

      <SectionAddBar
        items={missing.map((section) => ({
          name: section.name,
          label: t(section.labelKey),
          onAdd: () => reveal(section.name),
        }))}
      />
    </div>
  );
});

export default TaskSplitDetail;
